import { Injectable } from '@angular/core';

import { ErrorService } from '#services/error/error.service';
import { RequestOptionsBase } from '#models/db/request/request-options-base.model';
import { delay } from '#helpers/delay.helper';

export const MS_DELAY = 0;

@Injectable({
  providedIn: 'root',
})
export class IntermediaryService {
  constructor(private errorService: ErrorService) {}

  /**
   * Handles the intermediary process for a given repository function.
   *
   * This method manages the loading state, executes the repository function,
   * and updates the provided subject and next function with the response.
   * It also handles errors by setting an error instance in the error service.
   *
   * @template T - The type of the response expected from the repository function.
   * @param {RequestOptionsBase<T> | null} args - The request options which may include
   *                                              loading state, subject, and next function.
   * @param {() => Promise<T>} repositoryFn - The repository function to be executed.
   * @returns {Promise<void>} - A promise that resolves when the intermediary process is complete.
   */
  async intermediary<T>(
    args: RequestOptionsBase<T> | null,
    repositoryFn: () => Promise<T>
  ): Promise<void> {
    try {
      if (args?.loading) {
        args.loading.next(true);
      }

      const response = await repositoryFn();

      if (args?.delay) {
        await delay(Math.ceil(args.delay / 2));
      }

      if (args?.subject) {
        args.subject.next(response);
      }

      if (args?.loading) {
        args.loading.next(false);
      }

      if (args?.delay) {
        await delay(Math.ceil(args.delay / 2));
      }

      if (args?.nextFn) {
        args.nextFn(response);
      }
    } catch (error: unknown) {
      if (args?.delay) {
        await delay(Math.ceil(args.delay / 2));
      }

      if (args?.loading) {
        args.loading.next(false);
      }

      if (args?.delay) {
        await delay(Math.ceil(args.delay / 2));
      }

      this.errorService.setErrorInstance(error);
    }
  }
}
